import { useMemo } from 'react';
import { mapFindAllEventsResponseType, useFindAllEventsSuspense } from '@api';
import type { DateStringValue, ScheduleEventData } from '@mantine/schedule';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import type { EventType, PayloadType } from '../../types';
import {
  getScheduledEventFromAnnouncementData,
  getScheduledEventFromBdayData,
  getScheduledEventFromFestivalData,
  getScheduledEventFromShowsData,
} from './helpers';

type UseTouringScheduleEventsArgsType = {
  date: DateStringValue;
  filter: EventType[];
};

type UseTouringScheduleEventsReturnType = {
  events: ScheduleEventData<PayloadType>[];
};

export const useTouringScheduleEvents = ({
  date,
  filter,
}: UseTouringScheduleEventsArgsType): UseTouringScheduleEventsReturnType => {
  const { t } = useTranslation('dashboard');
  const viewYear = dayjs(date).year();

  const { data: eventsData } = useFindAllEventsSuspense(
    { year: viewYear },
    { query: { select: (data) => mapFindAllEventsResponseType(data) } }
  );

  const events = useMemo(() => {
    const getAnnouncementTitle = (artistName: string, tourName: string) =>
      t(`TOURING_SCHEDULE.ANNOUNCEMENT_TITLE`, {
        artist: artistName,
        tour: tourName,
      });

    return [
      ...(filter.includes('FESTIVAL')
        ? getScheduledEventFromFestivalData(eventsData.festivals)
        : []),
      ...(filter.includes('BDAY')
        ? getScheduledEventFromBdayData(eventsData.birthdays, viewYear)
        : []),
      ...(filter.includes('SHOW')
        ? getScheduledEventFromShowsData(eventsData.shows)
        : []),
      ...(filter.includes('ANNOUNCEMENT')
        ? getScheduledEventFromAnnouncementData(
            eventsData.announcements,
            getAnnouncementTitle
          )
        : []),
    ];
  }, [eventsData, filter, t, viewYear]);

  return { events };
};
